import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { BlogPost, BlogCategory, BlogPostsResponse, BlogPostResponse } from '../models/blog.model';

@Injectable({
    providedIn: 'root'
})
export class BlogService {
    private apiUrl = environment.apiUrl;

    constructor(private http: HttpClient) { }

    /**
     * Get list of published blog posts
     */
    getPosts(options: {
        limit?: number;
        offset?: number;
        category?: string;
        tag?: string;
        search?: string;
    } = {}): Observable<{ success: boolean; data: BlogPostsResponse }> {
        let params = new HttpParams();

        if (options.limit !== undefined) params = params.set('limit', Math.max(1, options.limit).toString());
        if (options.offset !== undefined) params = params.set('offset', Math.max(0, options.offset).toString());
        if (options.category) params = params.set('category', options.category);
        if (options.tag) params = params.set('tag', options.tag);
        if (options.search) params = params.set('search', options.search);

        return this.http.get<{ success: boolean; data: BlogPostsResponse }>(
            `${this.apiUrl}/blog/posts.php`,
            { params }
        );
    }

    /**
     * Get single post by slug
     */
    getPost(slug: string, userId?: string): Observable<{ success: boolean; data: BlogPostResponse }> {
        let params = new HttpParams().set('slug', slug);
        if (userId) {
            params = params.set('userId', userId);
        }

        return this.http.get<{ success: boolean; data: BlogPostResponse }>(
            `${this.apiUrl}/blog/post.php`,
            { params }
        );
    }

    /**
     * Get all categories
     */
    getCategories(): Observable<{ success: boolean; data: BlogCategory[] }> {
        return this.http.get<{ success: boolean; data: BlogCategory[] }>(
            `${this.apiUrl}/blog/categories.php`
        );
    }

    /**
     * Get trending posts
     */
    getTrending(limit: number = 5): Observable<{ success: boolean; data: BlogPost[] }> {
        return this.http.get<{ success: boolean; data: BlogPost[] }>(
            `${this.apiUrl}/blog/trending.php`,
            { params: { limit: limit.toString() } }
        );
    }

    /**
     * Subscribe to newsletter
     */
    subscribe(email: string): Observable<{ success: boolean; message: string }> {
        return this.http.post<{ success: boolean; message: string }>(
            `${this.apiUrl}/blog/subscribe.php`,
            { email }
        );
    }

    /**
     * Like/unlike a post
     */
    toggleLike(postId: number, userId?: string): Observable<{ success: boolean; data: { liked: boolean } }> {
        return this.http.post<{ success: boolean; data: { liked: boolean } }>(
            `${this.apiUrl}/blog/like.php`,
            { postId, userId }
        );
    }

    /**
     * Track analytics (scroll depth, time on page)
     */
    trackAnalytics(postId: number, data: {
        scroll_depth?: number;
        time_on_page?: number;
        referrer?: string;
        device_type?: 'desktop' | 'tablet' | 'mobile';
    }): Observable<any> {
        return this.http.post(
            `${this.apiUrl}/blog/track.php`,
            { post_id: postId, ...data }
        );
    }

    /**
     * Calculate reading time from content
     */
    calculateReadingTime(content: string): number {
        if (!content) return 0;
        const text = content.replace(/<[^>]*>/g, '').trim();
        if (!text) return 0;
        const wordsPerMinute = 200;
        const words = text.split(/\s+/).length;
        return Math.ceil(words / wordsPerMinute);
    }

    /**
     * Generate table of contents from HTML content
     */
    generateTableOfContents(content: string): { id: string; text: string; level: number }[] {
        const toc: { id: string; text: string; level: number }[] = [];
        // Regex to match h2-h4 tags and capture level, id (optional), and content
        // matches <h2 id="foo" class="bar">Title</h2> or <h3>Title</h3>
        // Match opening tag, attributes, content, closing tag
        const headingRegex = /<h([2-4])([^>]*)>(.*?)<\/h\1>/gi;
        const idRegex = /id=["']([^"']*)["']/i;
        let match;

        while ((match = headingRegex.exec(content)) !== null) {
            const level = parseInt(match[1], 10);
            const attrs = match[2];
            const contentText = match[3];

            // Extract ID from attributes
            const idMatch = idRegex.exec(attrs);
            const text = contentText.replace(/<[^>]*>/g, '').trim(); // Strip inner HTML from title

            const id = idMatch ? idMatch[1] : this.slugify(text);

            if (text) {
                toc.push({ id, text, level });
            }
        }

        return toc;
    }

    private slugify(text: string): string {
        return text
            .toLowerCase()
            .replace(/<[^>]*>/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    }
}
